<template>
  <table-wrapper>
    <v-data-table-server
      class="coronet-table--highlightable"
      :loading="loading"
      :headers="headers"
      :items="items"
      :fixed-header="true"
      :show-select="false"
      item-key="email"
      :items-per-page="pagination.pageSize"
      :items-length="totalItems"
      sort-asc-icon="$sortArrow"
      sort-desc-icon="$sortArrowDown"
      @update:options="
        $emit('page-changed', {
          pagination: { pageSize: $event.itemsPerPage, page: $event.page - 1 },
          sortingOptions: $event.sortBy,
        })
      "
    >
      <template #item="{ item, index }">
        <tr :key="index" :data-testid="`table-row-${index}`">
          <td :data-testid="`global-allow-list-table-name-col-${index}`">
            <div class="d-flex align-center name-col">
              <v-icon size="43" :icon="iconToTypeMap[item.type]"></v-icon>
              <span class="ml-4 coro-body2 text--semibold ellipsis d-inline-block">{{
                item.name
              }}</span>
            </div>
          </td>
          <td :data-testid="`global-allow-list-table-status-col-${index}`">
            <div :class="getStatusClass(item)">{{ getStatusName(item) }}</div>
          </td>
          <td :data-testid="`global-allow-list-table-description-col-${index}`">
            <ellipsified-copy-text
              v-if="item.description"
              width="300px"
              show-tooltip
              hide-copy-btn
              :text="item.description"
            />
            <span v-else>—</span>
          </td>
          <td
            :data-testid="`global-allow-list-table-emails-processed-col-${index}`"
            :class="{ 'text-indigo-medium': !item.emailsProcessedCount }"
          >
            {{ item.emailsProcessedCount ?? 0 }}
          </td>
          <td :data-testid="`global-allow-list-table-date-col-${index}`">
            {{ getFormattedDateTime(item.updateTimestamp) }}
          </td>
          <td class="text-right coro-table-col--sticky-right">
            <v-menu bottom left>
              <template #activator="{ props }">
                <v-btn
                  icon
                  :ripple="false"
                  :data-testid="`global-allow-list-table-remove-btn-${index}`"
                  variant="text"
                  v-bind="props"
                >
                  <v-icon icon="$dots" />
                </v-btn>
              </template>

              <v-list>
                <v-list-item
                  @click="
                    handleItemAction(
                      item.allowList
                        ? PhishingAction.REMOVE_FROM_ALLOWLIST
                        : PhishingAction.REMOVE_FROM_BLOCKLIST,
                      item
                    )
                  "
                >
                  {{ $t("phishingSettings.actions.remove") }}
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </template>
    </v-data-table-server>
  </table-wrapper>
</template>

<script lang="ts">
import TableWrapper from "@/components/TableWrapper.vue";
import EllipsifiedCopyText from "@/components/EllipsifiedCopyText.vue";
import { defineComponent, toRefs } from "vue";
import type { PropType } from "vue";
import { EmailSecurityAllowBlockItemType, PhishingAction } from "@/constants/phishing";
import { useI18n } from "vue-i18n";
import { usePageableTable } from "@/composables/usePageableTable";
import type { Pagination } from "@/types";
import type { GlobalAllowBlockListItem } from "@/_store/back-office/global-allow-block-list.module";
import { getFormattedDateTime } from "@/_helpers/date";

const iconToTypeMap: Partial<Record<EmailSecurityAllowBlockItemType, string>> = {
  [EmailSecurityAllowBlockItemType.DOMAIN]: "$locationAccess",
  [EmailSecurityAllowBlockItemType.IP]: "$locationAccess",
  [EmailSecurityAllowBlockItemType.IP_RANGE]: "$locationAccess",
  [EmailSecurityAllowBlockItemType.EMAIL]: "$email",
  [EmailSecurityAllowBlockItemType.TLD]: "$tld",
};

export default defineComponent({
  components: {
    TableWrapper,
    EllipsifiedCopyText,
  },
  props: {
    items: {
      type: Array as PropType<GlobalAllowBlockListItem[]>,
      required: true,
      default() {
        return [];
      },
    },
    pagination: {
      type: Object as PropType<Pagination>,
      required: true,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["on-item-action", "page-changed"],
  setup(props, { emit }) {
    const i18n = useI18n();

    const { totalItems, items, pagination } = toRefs(props);

    const { totalPages, page, handleListTablePaginationChange } = usePageableTable(
      {
        totalItems,
        items,
        pagination,
      },
      (value: Pagination) => {
        emit("page-changed", value);
      }
    );

    const getStatusName = (item: GlobalAllowBlockListItem) => {
      return item.allowList
        ? i18n.t("phishingSettings.allowed")
        : i18n.t("phishingSettings.blocked");
    };

    const getStatusClass = (item: GlobalAllowBlockListItem) => {
      return item.allowList ? "text-green-dark" : "text-red-dark";
    };

    const handleItemAction = (action: PhishingAction, item: GlobalAllowBlockListItem) => {
      const payload = {
        item,
        action,
      };
      emit("on-item-action", payload);
    };

    const headers = [
      {
        title: i18n.t("phishingSettings.table.name"),
        key: "name",
        sortable: false,
      },
      {
        title: i18n.t("phishingSettings.table.list"),
        key: "list",
        sortable: false,
      },
      {
        title: i18n.t("phishingSettings.table.description"),
        key: "description",
        sortable: false,
      },
      {
        title: i18n.t("phishingSettings.table.emailsProcessed"),
        key: "emailsProcessedCount",
        sortable: true,
        headerProps: {
          "data-testid": "global-allow-list-table-emails-processed-header",
        },
      },
      {
        title: i18n.t("phishingSettings.table.dateAdded"),
        key: "updateTimestamp",
        sortable: true,
      },
      {
        title: "",
        headerProps: {
          "aria-label": i18n.t("ariaLabels.actionsColumn"),
        },
      },
    ];

    return {
      getStatusName,
      getStatusClass,
      iconToTypeMap,
      headers,
      PhishingAction,
      allItemsSelected: false,
      handleItemAction,
      page,
      totalPages,
      handlePaginationChange: handleListTablePaginationChange,
      getFormattedDateTime,
    };
  },
});
</script>

<style scoped lang="scss">
:deep(*) {
  .v-data-table-rows-no-data {
    height: calc(100vh - 465px);
  }
}

.name-col {
  max-width: 500px;
}
</style>
